import './RulesModal.css'
import { useState, useRef, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import RulesLegend from './RulesLegend'
import { DEFAULT_RULES as N } from '../rules/config'

// The rules as the code plays them (src/rules, rules v4). Numbers come from
// rules/config so the text follows a tuning change.

// ─── RulesModal ───────────────────────────────────────────────────────────────
export default function RulesModal({ isVisible, onClose }) {
    const [legendOpen, setLegendOpen] = useState(false)
    const legendRef  = useRef(null)
    const toggleRef  = useRef(null)

    useEffect(() => {
        if (!isVisible) setLegendOpen(false)
    }, [isVisible])

    function handleLegendToggle() {
        const opening = !legendOpen
        setLegendOpen(opening)
        if (opening) {
            setTimeout(() => {
                toggleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }, 0)
        }
    }

    if (!isVisible) return null

    return (
        <div className="rm__overlay">
            <div className="rm__modal">

                {/* ── Header ── */}
                {/* ── Scrollable body ── */}
                <div className="rm__body">

                    {/* Banner header */}
                    <div className="rm__header">
                        <img src="/hog_how_to_play_banner.png" alt="" className="rm__header-banner" />
                        <div className="rm__header-text">
                            <span className="rm__header-sub">How to Play</span>
                            <span className="rm__header-title">Hog of War</span>
                        </div>
                    </div>

                    <p className="rm__intro">
                        Two Hives, each hidden from the other player, with 7 corridors running over both. Every round you and your opponent plan at the same time, then the round plays out. Find and destroy {N.structuresToWin} of your opponent’s structures before they destroy {N.structuresToWin} of yours.
                    </p>

                    <ol className="rm__rules">
                        <li>
                            <span className="rm__rule-name">Deploy</span>
                            {' '}— Place {N.armiesPerRound} new drones each round, on any corridors with room (you must place both). New drones defend straight away, but a corridor holding a new drone can’t attack that round. Next round they’re veterans.
                        </li>
                        <li>
                            <span className="rm__rule-name">Move</span>
                            {' '}— Each corridor can send one veteran one corridor left or right each round. Corridor 1 can only move right and corridor 7 only left. Only drones that started the round in a corridor can move, and never out of a corridor that’s attacking. Drones that move in still fight.
                        </li>
                        <li>
                            <span className="rm__rule-name">Attack</span>
                            {' '}— No attacks before round {N.firstAttackRound}. After that, you can only attack from a corridor where you had drones, or a mature brood, at the start of the round. Swipe up on the corridor (or tap its arrow), then tap the exact hex of their Hive to strike: it must be under that corridor. Strength is your drones plus 2 for each mature brood. The higher strength wins; equal strength is a tie, and no strike lands. Any battle, won, lost or tied, wipes out every drone in that corridor on both sides.
                        </li>
                        <li>
                            <span className="rm__rule-name">Strikes</span>
                            {' '}— The winner strikes the loser’s Hive, and the bigger the margin, the bigger the blast: 1 → 7 hexes, 2 → 13, 3 → 19, 4 → 25, 5 → 31, 6 → 37, 7 or more → 43. A blast can spread into the neighbouring corridors. If the defender wins, they strike back in that corridor automatically.
                        </li>
                        <li>
                            <span className="rm__rule-name">Clues</span>
                            {' '}— A strike reveals the hexes it destroys, and stamps a clue on the hex you aimed at: A:3 means structure A is 3 hexes away, the nearest structure still standing after the blast. Hold the eye in the header to hide the drones and corridors and see every clue’s ring, on both Hives, so you also see what your opponent knows about you. A clue turns grey once its structure is destroyed.
                        </li>
                        <li>
                            <span className="rm__rule-name">Structures</span>
                            {' '}— Each Hive has {N.homefrontMaxStructures}. A structure is destroyed only when every one of its hexes is destroyed; a damaged structure still stands. The scoreboard in the header has {N.structuresToWin} icons a side, crossed out as structures fall. Lose {N.structuresToWin} and you lose the war. If both sides lose their {N.structuresToWin === 3 ? 'third' : `${N.structuresToWin}th`} in the same round, it’s a draw. There’s no round limit.
                        </li>
                        <li>
                            <span className="rm__rule-name">Broods</span>
                            {' '}— Spend {N.broodCost} veterans that started the round in the same corridor: drag one onto the other, then slide the brood to a row. It’s a 3-hex bar on your Hive, centred in that corridor. It takes {N.fortMaturationRounds} rounds to build, and while it’s under construction it’s worth 0, a single destroyed hex destroys it, and your opponent can see it. Mature, it’s worth {N.fortStrength} (one for each end hex still standing) when attacking or defending. Defending, it holds its fire if your drones alone can match the attack. A brood that fires is used up. Broods take no room in the corridor, never move, don’t count toward the {N.structuresToWin} structures, and clues ignore them.
                        </li>
                        <li>
                            <span className="rm__rule-name">Eggs</span>
                            {' '}— Each round a mature brood isn’t attacking, it lays an egg: before you submit, place its drop zone on any corridor with room. After the battles the egg is launched there and lands as a new drone (so that corridor can’t attack next round). No egg lands if the brood fired or died, or the corridor is full. If any hex of a brood is destroyed, it never lays again.
                        </li>
                        <li>
                            <span className="rm__rule-name">Yield, rockets and recon planes</span>
                            {' '}— Each lot on your Hive earns {N.yieldPerLotPerRound} yield a round, and {N.extractorYieldMultiplier}× that while its extractor stands on its network of same-type lots; a destroyed lot earns nothing and cuts its network. Yield is paid after the round; you start with {N.startingYield}. A rocket ({N.rocketCost}) sits on one of your structure’s hexes and protects every hex within {N.rocketCoverageSteps} steps: if a strike is aimed into its cover, it cancels one shot of the margin, then it’s gone. It works the round you place it. A recon plane ({N.droneCost}) launches from one of your structures and scans 4 hexes in that corridor of their Hive, leaving a clue; it flies after the battles and can’t be stopped.
                        </li>
                    </ol>

                    {/* ── Legend toggle ── */}
                    <button
                        className="rm__legend-toggle"
                        onClick={handleLegendToggle}
                        ref={toggleRef}
                    >
                        Board legend
                        <FontAwesomeIcon icon={legendOpen ? faChevronUp : faChevronDown} className="rm__legend-caret" />
                    </button>

                    {legendOpen && <div ref={legendRef}><RulesLegend /></div>}
                </div>

                {/* ── Footer / play button ── */}
                <div className="rm__footer">
                    <button className="rm__play-btn" onClick={onClose}>
                        Done
                    </button>
                </div>

            </div>
        </div>
    )
}
